import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  startAfter,
  updateDoc,
  where,
} from 'firebase/firestore';
import { db } from '../firebase/firebase-config';
import QnAAnswer from '../models/QnAAnswer';
import QnAQuestion from '../models/QnAQuestion';
import { COLLECTIONS } from '../platform/constants';
import { LAST_READ_MODULES } from '../platform/lastRead/lastReadModules';
import { markItemAsRead } from '../platform/lastRead/lastReadService';
import { QNA_ANSWER_TYPE, QNA_STATUS } from '../platform/qnaConstants';
import { canViewAllQuestionUnits } from '../platform/qnaPermissions';
import { withTenantScope } from './contentWriteFields';
import { getVisibleUnitIds } from './contentQueryHelpers';
import {
  applyTenantScopeToCollection,
  buildCreatedAtListQuery,
  mapAndFilterSnapshot,
} from './tenantListQuery';

export const QUESTIONS_PAGE_SIZE = 20;

const ANSWERS_SUBCOLLECTION = 'answers';
const LEGACY_MESSAGES_SUBCOLLECTION = 'messages';

function questionsCollection() {
  return collection(db, COLLECTIONS.QUESTIONS);
}

function answersCollection(questionId) {
  return collection(db, COLLECTIONS.QUESTIONS, questionId, ANSWERS_SUBCOLLECTION);
}

function legacyMessagesCollection(questionId) {
  return collection(db, COLLECTIONS.QUESTIONS, questionId, LEGACY_MESSAGES_SUBCOLLECTION);
}

function buildQuestionsListQuery(user, { tenantId, memberships, pageSize, lastVisible }) {
  if (user && !canViewAllQuestionUnits(memberships, tenantId, user)) {
    const scoped = applyTenantScopeToCollection(COLLECTIONS.QUESTIONS, tenantId);
    const constraints = [
      where('unit', 'in', getVisibleUnitIds(user)),
      orderBy('createdAt', 'desc'),
    ];
    if (lastVisible) {
      constraints.push(startAfter(lastVisible));
    }
    if (pageSize) {
      constraints.push(limit(pageSize));
    }
    return query(scoped, ...constraints);
  }

  return buildCreatedAtListQuery({
    collectionName: COLLECTIONS.QUESTIONS,
    tenantId,
    user,
    memberships,
    pageSize,
    lastVisible,
  });
}

export function buildQuestionsPageQuery(user, tenantId, memberships, options = {}) {
  return buildQuestionsListQuery(user, {
    tenantId,
    memberships,
    pageSize: options.pageSize ?? QUESTIONS_PAGE_SIZE,
    lastVisible: options.startAfterDoc ?? null,
  });
}

export async function fetchQuestionsPage(user, tenantId, memberships, options = {}) {
  const listQuery = buildQuestionsPageQuery(user, tenantId, memberships, options);
  const querySnapshot = await getDocs(listQuery);
  return {
    questions: mapAndFilterSnapshot(
      querySnapshot,
      tenantId,
      (docSnap) => QnAQuestion.fromFirestore(docSnap),
    ),
    lastVisible: querySnapshot.docs[querySnapshot.docs.length - 1] || null,
  };
}

export async function deleteQuestion(questionId) {
  await deleteDoc(doc(db, COLLECTIONS.QUESTIONS, questionId));
}

export async function markQuestionAsRead(userId, questionId) {
  await markItemAsRead(userId, LAST_READ_MODULES.QNA, questionId);
}

export function subscribeQuestions(user, {
  tenantId,
  memberships,
  pageSize = QUESTIONS_PAGE_SIZE,
  onData,
  onError,
}) {
  const listQuery = buildQuestionsListQuery(user, { tenantId, memberships, pageSize });

  return onSnapshot(
    listQuery,
    (querySnapshot) => {
      onData({
        questions: mapAndFilterSnapshot(
          querySnapshot,
          tenantId,
          (docSnap) => QnAQuestion.fromFirestore(docSnap),
        ),
        lastVisible: querySnapshot.docs[querySnapshot.docs.length - 1] || null,
      });
    },
    onError,
  );
}

export async function createQuestion(activeTenantId, question) {
  const payload = withTenantScope(activeTenantId, {
    ...question.toFirestore(),
    status: question.status || QNA_STATUS.OPEN,
    createdAt: serverTimestamp(),
    lastActivityAt: serverTimestamp(),
  });
  const docRef = await addDoc(questionsCollection(), payload);
  return docRef.id;
}

export async function updateQuestionContent(questionId, { title, body, unit, unitName }) {
  await updateDoc(doc(db, COLLECTIONS.QUESTIONS, questionId), {
    title,
    body,
    unit,
    unitName,
  });
}

async function touchQuestionActivity(questionId) {
  await updateDoc(doc(db, COLLECTIONS.QUESTIONS, questionId), {
    lastActivityAt: serverTimestamp(),
  });
}

export async function addQuestionAnswer(activeTenantId, questionId, answer) {
  const docRef = await addDoc(
    answersCollection(questionId),
    withTenantScope(activeTenantId, {
      ...answer.toFirestore(),
      createdAt: serverTimestamp(),
    }),
  );
  await touchQuestionActivity(questionId);
  return docRef.id;
}

export async function updateQuestionAnswer(questionId, answerId, answer) {
  await updateDoc(
    doc(db, COLLECTIONS.QUESTIONS, questionId, ANSWERS_SUBCOLLECTION, answerId),
    {
      text: answer.text,
      displayName: answer.displayName,
    },
  );
  await touchQuestionActivity(questionId);
}

export async function updateOfficialAnswer({
  questionId,
  answerId,
  text,
  user,
}) {
  const displayName = user.displayName || 'Uporabnik';
  const answerPayload = new QnAAnswer({
    text,
    uid: user.uid,
    displayName,
    type: QNA_ANSWER_TYPE.OFFICIAL,
  });

  await updateQuestionAnswer(questionId, answerId, answerPayload);
  return answerId;
}

export async function publishOfficialAnswer({
  activeTenantId,
  questionId,
  text,
  user,
}) {
  const displayName = user.displayName || 'Uporabnik';
  const answerPayload = new QnAAnswer({
    text,
    uid: user.uid,
    displayName,
    type: QNA_ANSWER_TYPE.OFFICIAL,
  });

  const answerId = await addQuestionAnswer(activeTenantId, questionId, answerPayload);

  await updateDoc(doc(db, COLLECTIONS.QUESTIONS, questionId), {
    status: QNA_STATUS.ANSWERED,
    answeredAt: serverTimestamp(),
    answeredByUid: user.uid,
    answeredByName: displayName,
    lastActivityAt: serverTimestamp(),
  });

  return answerId;
}

export async function addQuestionFollowUp(activeTenantId, questionId, { text, user }) {
  const answer = new QnAAnswer({
    text,
    uid: user.uid,
    displayName: user.displayName || 'Uporabnik',
    type: QNA_ANSWER_TYPE.FOLLOWUP,
  });
  return addQuestionAnswer(activeTenantId, questionId, answer);
}

export async function closeQuestion(questionId) {
  await updateDoc(doc(db, COLLECTIONS.QUESTIONS, questionId), {
    status: QNA_STATUS.CLOSED,
  });
}

function mapAnswersSnapshot(querySnapshot, tenantId, source) {
  return mapAndFilterSnapshot(
    querySnapshot,
    tenantId,
    (docSnap) => QnAAnswer.fromFirestore(docSnap, { source }),
  );
}

export function subscribeQuestionThread(activeTenantId, questionId, onData, onError) {
  const state = { answers: [], legacy: [] };

  const emit = () => {
    const answerIds = new Set(state.answers.map((item) => item.id));
    const legacy = state.legacy.filter((item) => !answerIds.has(item.id));
    const merged = [...state.answers, ...legacy].sort(
      (a, b) => (a.createdAt?.getTime?.() || 0) - (b.createdAt?.getTime?.() || 0),
    );
    onData(merged);
  };

  const answersQuery = query(answersCollection(questionId), orderBy('createdAt', 'asc'));
  const legacyQuery = query(legacyMessagesCollection(questionId), orderBy('createdAt', 'asc'));

  const unsubscribeAnswers = onSnapshot(
    answersQuery,
    (querySnapshot) => {
      state.answers = mapAnswersSnapshot(querySnapshot, activeTenantId, 'answers');
      emit();
    },
    onError,
  );

  const unsubscribeLegacy = onSnapshot(
    legacyQuery,
    (querySnapshot) => {
      state.legacy = mapAnswersSnapshot(querySnapshot, activeTenantId, 'messages')
        .map((item) => new QnAAnswer({
          ...item,
          type: item.type === QNA_ANSWER_TYPE.OFFICIAL || item.type === QNA_ANSWER_TYPE.FOLLOWUP
            ? item.type
            : QNA_ANSWER_TYPE.LEGACY,
          source: 'messages',
        }));
      emit();
    },
    onError,
  );

  return () => {
    unsubscribeAnswers();
    unsubscribeLegacy();
  };
}

export function getOfficialAnswers(answers) {
  return answers
    .filter((answer) => answer.type === QNA_ANSWER_TYPE.OFFICIAL)
    .sort((a, b) => (a.createdAt?.getTime?.() || 0) - (b.createdAt?.getTime?.() || 0));
}

export function getOfficialAnswer(answers) {
  const officials = getOfficialAnswers(answers);
  return officials[officials.length - 1] || null;
}

